
import { useState, useCallback } from 'react';
import { Courier } from '@/services/types';
import { PaymentMethod } from '../types/collectionTypes';
import { toast } from '@/components/ui/sonner';
import { submitCollectionRequest } from '../services/collectionService';
import { fetchCourierPaymentMethods } from '../services/paymentService';
import { useCollectionForm } from '../context/CollectionFormContext';
import { TransactionStatus } from '../components/CollectionStatusDisplay';

interface CollectionDataOptions {
  onSuccess?: () => void;
}

export const useCollectionData = (options: CollectionDataOptions = {}) => {
  const { onSuccess } = options;
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  
  const { 
    setPaymentMethods: setContextPaymentMethods,
    setSubmitting,
    setStatusMessage,
    setTransactionId,
    setTransactionStatus,
    filteredCouriers // <-- Используем курьеров из контекста
  } = useCollectionForm();
  
  // Загрузка платежных методов курьера
  const loadCourierPaymentData = useCallback(async (courierId: string) => {
    try {
      const methods = await fetchCourierPaymentMethods(courierId);
      setPaymentMethods(methods);
      setContextPaymentMethods(methods);
      console.log('Загружены платежные методы:', methods.length, 'для курьера:', courierId);
      return methods;
    } catch (error) {
      console.error('Ошибка при загрузке платежных методов:', error);
      toast.error('Не удалось загрузить платежные методы курьера');
      setPaymentMethods([]);
      setContextPaymentMethods([]);
      return [];
    }
  }, [setContextPaymentMethods]);
  
  // Функция для валидации и преобразования статуса
  const validateTransactionStatus = (status: string | undefined): TransactionStatus => {
    const validStatuses: TransactionStatus[] = ['loading', 'success', 'error', 'auth_success', 'processing'];
    if (status && validStatuses.includes(status as TransactionStatus)) {
      return status as TransactionStatus;
    }
    return 'success'; // дефолтное значение
  };
  
  // Обработка отправки формы взыскания
  const handleSubmit = useCallback(async (data: any) => {
    setSubmitting(true);
    setTransactionStatus('loading');
    
    try {
      console.log('Начинаем обработку взыскания:', data);
      console.log('Используем данные курьеров:', filteredCouriers.length, 'записей');
      
      // Отправляем запрос на создание взыскания
      const result = await submitCollectionRequest(
        data.courierId,
        data.paymentMethodId,
        data.amount,
        data.reason,
        filteredCouriers, // <-- Используем курьеров из контекста
        paymentMethods,
        data.isAuthRequest
      );
      
      console.log('Результат отправки вебхука:', result);
      
      if (result.success) {
        // Показываем статус успешной операции
        const validatedStatus = validateTransactionStatus(result.status);
        setTransactionStatus(validatedStatus);
        setStatusMessage(result.message || 'Операция выполнена успешно');
        if (result.transactionId) {
          setTransactionId(result.transactionId);
        }
        
        // Вызываем колбэк успешной операции, но не закрываем форму,
        // так как теперь показываем красивый статус
        if (onSuccess) {
          console.log('Вызываем onSuccess колбэк для дополнительных действий');
          onSuccess();
        }
        return true;
      } else {
        setTransactionStatus('error');
        setStatusMessage(result.message || 'Ошибка при выполнении операции');
        return false;
      }
    } catch (error: any) {
      console.error('Ошибка при создании взыскания:', error);
      setTransactionStatus('error');
      setStatusMessage(error.message || 'Неизвестная ошибка при создании взыскания');
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [filteredCouriers, paymentMethods, setSubmitting, setTransactionStatus, setStatusMessage, setTransactionId, onSuccess]);
  
  return {
    paymentMethods,
    setPaymentMethods,
    loadCourierPaymentData,
    handleSubmit,
  };
};
